import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { listAdminDisputes, resolveDispute } from "@/lib/admin.functions";
import { getIsAdmin } from "@/lib/payments.functions";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ExportReportButton } from "@/components/ExportReportButton";
import { DisputeSlaBadge } from "@/components/DisputeSlaBadge";

export const Route = createFileRoute("/_authenticated/admin/disputes")({
  head: () => ({ meta: [{ title: "Admin Disputes — The Hospitality Reserve" }] }),
  component: AdminDisputes,
});

type Item = Awaited<ReturnType<typeof listAdminDisputes>>["items"][number];
type RefundChoice = "none" | "venue_full" | "venue_partial" | "bartender";

function AdminDisputes() {
  const navigate = useNavigate();
  const checkAdmin = useServerFn(getIsAdmin);
  const load = useServerFn(listAdminDisputes);
  const resolve = useServerFn(resolveDispute);
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState<{ item: Item; decision: "resolved" | "dismissed" } | null>(null);
  const [note, setNote] = useState("");
  const [refundChoice, setRefundChoice] = useState<RefundChoice>("none");
  const [partialAmount, setPartialAmount] = useState<string>("");
  const [acting, setActing] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try { const r = await load(); setItems(r.items); }
    catch (e) { toast.error(e instanceof Error ? e.message : "Failed to load disputes."); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    (async () => {
      const a = await checkAdmin();
      if (!a.isAdmin) { navigate({ to: "/dashboard", replace: true }); return; }
      await refresh();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openResolve = (item: Item, decision: "resolved" | "dismissed") => {
    setResolving({ item, decision });
    setNote("");
    setRefundChoice("none");
    setPartialAmount("");
  };

  const confirmResolve = async () => {
    if (!resolving || !resolving.item.manual_id) return;
    const target = resolving.item.refund_target;
    setActing(true);
    try {
      let refund: { booking_id: string; recipient: "venue" | "bartender"; amount_pence?: number } | undefined;
      if (refundChoice !== "none") {
        if (!target) { toast.error("No paid booking found on this shift to refund."); setActing(false); return; }
        if (!target.refundable && refundChoice !== "bartender") {
          toast.error("Shift is not in a refundable state. Use bartender payout instead.");
          setActing(false); return;
        }
        if (refundChoice === "venue_full") {
          refund = { booking_id: target.booking_id, recipient: "venue", amount_pence: target.total_pence };
        } else if (refundChoice === "venue_partial") {
          const pence = Math.round(Number(partialAmount) * 100);
          if (!pence || pence <= 0 || pence > target.total_pence) {
            toast.error(`Partial amount must be between £0.01 and £${(target.total_pence / 100).toFixed(2)}.`);
            setActing(false); return;
          }
          refund = { booking_id: target.booking_id, recipient: "venue", amount_pence: pence };
        } else {
          const pence = partialAmount.trim()
            ? Math.round(Number(partialAmount) * 100)
            : target.total_pence;
          if (!pence || pence <= 0) { toast.error("Payout amount must be > 0."); setActing(false); return; }
          refund = { booking_id: target.booking_id, recipient: "bartender", amount_pence: pence };
        }
      }
      const r = await resolve({ data: { manual_id: resolving.item.manual_id, decision: resolving.decision, note: note.trim() || undefined, refund } });
      if ("error" in r) throw new Error(r.error);
      if ("refund_error" in r && r.refund_error) toast.error(`Resolved, but refund failed: ${r.refund_error}`);
      else if ("refund" in r && r.refund) toast.success(`Resolved. Refund of £${(r.refund.amount_pence / 100).toFixed(2)} (${r.refund.status}).`);
      else toast.success(resolving.decision === "resolved" ? "Dispute marked resolved." : "Dispute dismissed.");
      setResolving(null); setNote("");
      await refresh();
    } catch (e) { toast.error(e instanceof Error ? e.message : "Failed."); }
    finally { setActing(false); }
  };

  const kindLabel = (k: Item["kind"]) =>
    k === "manual" ? "Manual dispute" : k === "cancellation" ? "Paid cancellation" : "Confirmation mismatch";
  const kindClass = (k: Item["kind"]) =>
    k === "manual" ? "text-destructive" : k === "cancellation" ? "text-accent" : "text-muted-foreground";

  const target = resolving?.item.refund_target;

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader role="admin" />
      <main className="mx-auto max-w-6xl px-6 py-10">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <p className="tracking-wide-luxury text-accent">Admin Disputes</p>
            <h1 className="mt-3 font-display text-3xl">Resolution Queue</h1>
          </div>
          <div className="flex gap-2">
            <Link to="/admin" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">Console</Link>
            <Link to="/admin/analytics" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">Analytics</Link>
            <Link to="/admin/arrival-disputes" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">Arrival Disputes</Link>
            <ExportReportButton scope="all" />
          </div>
        </div>
        <span className="gold-divider mt-4" />
        <p className="mt-4 text-sm text-muted-foreground">
          User-raised disputes, paid-booking cancellations, and post-shift confirmation mismatches.
          Resolutions on manual disputes can issue a Stripe refund to the venue or a compensatory payout to the bartender.
        </p>

        {loading ? (
          <p className="mt-12 text-center text-sm text-muted-foreground">Loading…</p>
        ) : items.length === 0 ? (
          <div className="mt-10 border border-border bg-card p-8 text-center">
            <p className="font-display text-lg">All clear.</p>
            <p className="mt-2 text-sm text-muted-foreground">Nothing requires admin attention.</p>
          </div>
        ) : (
          <div className="mt-8 space-y-4">
            {items.map((d) => (
              <article key={d.id} className="border border-border bg-card p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className={`text-xs tracking-wide-luxury ${kindClass(d.kind)}`}>{kindLabel(d.kind)}</p>
                      {d.kind === "manual" && (
                        <DisputeSlaBadge createdAt={d.created_at} escalatedAt={d.escalated_at ?? null} />
                      )}
                    </div>
                    <p className="mt-2 font-display text-lg">{d.summary}</p>
                    <p className="mt-1 text-xs text-muted-foreground">Venue: {d.venue_name}</p>
                    {d.shift && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {new Date(d.shift.start_time).toLocaleString()} — {new Date(d.shift.end_time).toLocaleTimeString()} · {d.shift.address || "—"} · {d.shift.status} · payment {d.shift.payment_status ?? "—"}
                      </p>
                    )}
                    {d.refund_target && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        Refundable: £{(d.refund_target.total_pence / 100).toFixed(2)} · {d.refund_target.refundable ? "card refund available" : "card refund not available — payout only"}
                      </p>
                    )}
                    <p className="mt-3 text-sm whitespace-pre-wrap">{d.detail}</p>
                  </div>
                  <div className="flex flex-col gap-2 items-end">
                    <Link to="/shift/$id/notes" params={{ id: d.shift_id }} className="border border-border px-3 py-2 text-xs tracking-wide-luxury hover:border-accent">
                      View notes
                    </Link>
                    {d.kind === "manual" && d.manual_id && (
                      <>
                        <Button onClick={() => openResolve(d, "resolved")}
                          className="rounded-none bg-accent text-accent-foreground hover:bg-accent/90 tracking-wide-luxury">
                          Resolve…
                        </Button>
                        <Button variant="outline" onClick={() => openResolve(d, "dismissed")}
                          className="rounded-none border-destructive text-destructive hover:bg-destructive/10 tracking-wide-luxury">
                          Dismiss…
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>

      <Dialog open={!!resolving} onOpenChange={(o) => { if (!o) { setResolving(null); setNote(""); } }}>
        <DialogContent className="rounded-none border-border bg-card max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {resolving?.decision === "resolved" ? "Resolve dispute" : "Dismiss dispute"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="text-xs tracking-wide-luxury">Resolution note (internal audit record)</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value.slice(0, 2000))} rows={4}
                placeholder="What was decided and why?" className="rounded-none mt-1" />
            </div>

            <div className="border border-border p-3">
              <p className="text-xs tracking-wide-luxury text-accent mb-2">Financial action</p>
              {!target ? (
                <p className="text-xs text-muted-foreground">No paid booking detected for this shift — financial action not available.</p>
              ) : (
                <div className="space-y-2 text-sm">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="radio" name="refund" checked={refundChoice === "none"} onChange={() => setRefundChoice("none")} />
                    <span>No refund or payout — money stays as currently allocated.</span>
                  </label>
                  <label className={`flex items-start gap-2 ${target.refundable ? "cursor-pointer" : "opacity-50 cursor-not-allowed"}`}>
                    <input type="radio" name="refund" disabled={!target.refundable} checked={refundChoice === "venue_full"} onChange={() => setRefundChoice("venue_full")} />
                    <span>Full refund to venue (£{(target.total_pence / 100).toFixed(2)})</span>
                  </label>
                  <label className={`flex items-start gap-2 ${target.refundable ? "cursor-pointer" : "opacity-50 cursor-not-allowed"}`}>
                    <input type="radio" name="refund" disabled={!target.refundable} checked={refundChoice === "venue_partial"} onChange={() => setRefundChoice("venue_partial")} />
                    <span>Partial refund to venue</span>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="radio" name="refund" checked={refundChoice === "bartender"} onChange={() => setRefundChoice("bartender")} />
                    <span>Compensatory payout to bartender</span>
                  </label>

                  {(refundChoice === "venue_partial" || refundChoice === "bartender") && (
                    <div className="pt-2">
                      <Label className="text-xs">Amount (GBP){refundChoice === "bartender" ? " — leave blank for full shift total" : ""}</Label>
                      <Input type="number" step="0.01" min="0.01" value={partialAmount}
                        onChange={(e) => setPartialAmount(e.target.value)}
                        placeholder={refundChoice === "venue_partial" ? `Max £${(target.total_pence / 100).toFixed(2)}` : `£${(target.total_pence / 100).toFixed(2)}`}
                        className="rounded-none mt-1" />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setResolving(null); setNote(""); }} className="rounded-none tracking-wide-luxury">Cancel</Button>
            <Button onClick={confirmResolve} disabled={acting}
              className="rounded-none bg-accent text-accent-foreground hover:bg-accent/90 tracking-wide-luxury">
              {acting ? "Working…" : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
